import { z } from 'zod'

import type { RuleGroupType } from 'react-querybuilder'

/**
 * Integration selection: `id` = connector definition id, `ev` = event type id.
 */
export const searchIntegrationsSchema = z.array(
	z.object({
		id: z.string(),
		ev: z.string(),
	}),
)

const timeRangePresetSchema = z.enum([
	'last-15m',
	'last-1h',
	'last-24h',
	'last-7d',
	'last-30d',
])

const absoluteTimeRangeSchema = z.object({
	st: z.string(),
	ed: z.string(),
})

const presetTimeRangeSchema = z.object({
	preset: timeRangePresetSchema,
})

/**
 * Time range — either a rolling preset (resolves to absolute at render) or
 * an explicit absolute window. ISO 8601 UTC strings ending in 'Z'.
 */
export const searchTimeRangeSchema = z.union([
	presetTimeRangeSchema,
	absoluteTimeRangeSchema,
])

export const filterOperatorSchema = z.enum([
	'eq',
	'neq',
	'in',
	'not_in',
	'gt',
	'lt',
	'gte',
	'lte',
	'contains',
	'exists',
])

/**
 * Structured filter. Lives inside `ft` URL entries.
 * - `f` = canonical field id (e.g. `event.userName`, `normalized_fields.username`)
 * - `o` = operator
 * - `v` = single value or array (for `in` / `not_in`)
 */
export const structuredFilterSchema = z.object({
	f: z.string(),
	o: filterOperatorSchema,
	v: z.union([z.string(), z.array(z.string())]),
})

/**
 * Zod Schema for the Search page URL params. Short keys keep the URL terse.
 *
 * Breaking changes from v1: `q` is now a native object (not JSON.stringify'd),
 * `ft` is structured, `tr` accepts presets, `cid` removed.
 *
 * `q` uses `z.custom<RuleGroupType>()` instead of `z.any()` so consumers get
 * full RuleGroupType inference without the runtime validation overhead of a
 * deep schema (RQB's shape is recursive and large).
 */
export const searchSchema = z.object({
	int: searchIntegrationsSchema.optional(),
	q: z.custom<RuleGroupType>().optional(),
	tr: searchTimeRangeSchema.optional(),
	ft: z.array(structuredFilterSchema).optional(),
})

export type SearchParams = z.infer<typeof searchSchema>

export type SearchIntegrations = NonNullable<
	z.infer<typeof searchIntegrationsSchema>
>

export type PrettyfiedSearchParams = Omit<SearchParams, 'int'> & {
	integrations: Array<string>
	events: Array<string>
}

export type SearchTimeRange = z.infer<typeof searchTimeRangeSchema>
export type SearchFilter = z.infer<typeof structuredFilterSchema>
export type FilterOperator = z.infer<typeof filterOperatorSchema>

/**
 * Saved search payload format. The `version: 2` discriminator lets us safely
 * ignore legacy v1 rows (decodeSavedSearch returns null when version !== 2).
 */
export const savedSearchPayloadSchema = z.object({
	version: z.literal(2),
	int: searchIntegrationsSchema.optional(),
	tr: searchTimeRangeSchema.optional(),
	q: z.custom<RuleGroupType>().optional(),
	ft: z.array(structuredFilterSchema).optional(),
})

export type SavedSearchPayload = z.infer<typeof savedSearchPayloadSchema>

export type QuickSelectUnit =
	| 'second'
	| 'minute'
	| 'hour'
	| 'day'
	| 'week'
	| 'month'
	| 'year'

export type EventTypeSelection = {
	definitionId: string
	eventType: string
}

export type DefinitionOption = {
	value: string
	label: string
}

export type EventTypeOption = {
	value: string
	label: string
	definitionId: string
	definitionName: string
}

export type SavedSearchResponse = {
	saved_searches: Array<SavedSearch>
}

export type SavedSearch = {
	tenant: string
	name: string
	created_by_username: string
	updated_by_username: string
	created_at: string
	updated_at: string
	search_string: string
}
